import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { AuthenticationService } from '../services/authentication.service';
import { OnboardingService } from '../services/onboarding.service';
import { SubscriptionTransferService } from '../services/subscription-transfer.service';
import { isTransferFrozen } from '../components/onboarding/onboarding-phase.util';

/**
 * Blocks access to the rest of the app while a parent/educator's subscription
 * transfer is pending — the account stays frozen until the student accepts
 * or the transfer is cancelled, so every guarded route bounces to the frozen
 * screen regardless of onboardingGuard's narrower (dashboard/free-taster
 * only) "nudge to finish onboarding" behaviour. Checks the real transfer
 * record (not the onboarding wizard's own `transfer` answer), since a
 * transfer can also be started from the account settings page directly.
 */
export const transferFrozenGuard: CanActivateFn = async () => {
  const authSvc = inject(AuthenticationService);

  if (!authSvc.isUserLoggedIn()) {
    return true; // authenticationGuard handles the redirect to /login
  }

  const onboardingSvc = inject(OnboardingService);
  const subscriptionTransferSvc = inject(SubscriptionTransferService);
  const router = inject(Router);

  try {
    const [onboarding, transferStatus] = await Promise.all([
      lastValueFrom(onboardingSvc.getOnboarding()),
      lastValueFrom(subscriptionTransferSvc.getTransferStatus('')),
    ]);
    if (isTransferFrozen(onboarding.role, transferStatus)) {
      return router.parseUrl('/onboarding/frozen');
    }
  } catch (err) {
    // best-effort — don't block access to the app on a failed check
    console.error('Transfer status check failed:', err);
  }

  return true;
};
